import type { DayResult } from '../types'
import { ragLabel } from './ragStyles'

export function formatMoney(amount: number, symbol = '£'): string {
  const sign = amount < 0 ? '-' : ''
  const abs = Math.abs(amount)
  return `${sign}${symbol}${abs.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatSigned(amount: number, symbol = '£'): string {
  const sign = amount > 0 ? '+' : amount < 0 ? '-' : ''
  const abs = Math.abs(amount)
  return `${sign}${symbol}${abs.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Whole pounds, for cells too narrow for the pence.
 *
 * A phone gives each day of the month about fifty pixels; "£1,910.00" doesn't
 * fit and gets clipped mid-number, which is worse than rounding. The full
 * figure is still a tap away in the day breakdown.
 */
export function formatMoneyCompact(amount: number, symbol = '£'): string {
  const sign = amount < 0 ? '-' : ''
  return `${sign}${symbol}${Math.round(Math.abs(amount)).toLocaleString('en-GB')}`
}

export function formatDateShort(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * A calendar day as a screen reader should hear it. The cell itself shows the
 * date, a transaction count and a balance as separate bits of text, which
 * read out as one run-together number ("271£1,455.00").
 */
export function describeDay(date: Date, result: DayResult | undefined, currencySymbol: string): string {
  const when = date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
  if (!result) return `${when}: before the starting balance date`

  const parts = [`${when}: closing balance ${formatMoney(result.balance, currencySymbol)}`, ragLabel[result.rag].toLowerCase()]
  const count = result.transactions.length
  if (count > 0) parts.push(`${count} ${count === 1 ? 'transaction' : 'transactions'}`)
  for (const hit of result.targetHits) parts.push(`${hit.name} reached`)
  return parts.join(', ')
}
